/**
 * Renewal work-details pre-fill for the /b/ WorkDetails form.
 *
 * cpcore returns `previousEmployment` (the tenant's most recent PREVIOUS journey's
 * employment, read-only, never the current journey's own row) as part of the same
 * `b2b/onboard/pre-flight` payload the app already fetches on every load — see
 * `LeadService.getPreviousEmploymentForPrefill` / `findLead` in cpcore. It is `null`/
 * absent for a first-time tenant, so this module never invents a "renewal" signal of
 * its own — presence of `previousEmployment` IS the renewal signal.
 *
 * ONLY these fields may ever be copied onto the form. There is deliberately no
 * verification field here (no `verifiedWorkEmail`, no ops-override fields) — cpcore
 * never sends them for this purpose, and even if it did, this whitelist would drop
 * them. A returning tenant re-verifies the work email every journey; pre-fill may
 * carry the ADDRESS, never the verified state.
 *
 * Dependency-free; see the node:test file next to this one.
 */
export const PREFILLABLE_WORK_DETAILS_FIELDS = [
  "companyName",
  "designation",
  "officeAddress",
  "workEmail",
  "income",
];

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

/** True when there is anything worth pre-filling from a previous journey. */
export function hasPrefillableEmployment(previousEmployment) {
  if (!previousEmployment) return false;
  return (
    PREFILLABLE_WORK_DETAILS_FIELDS.some((field) =>
      hasValue(previousEmployment[field]),
    ) || hasValue(previousEmployment.employmentType)
  );
}

/**
 * Merges `previousEmployment` onto `currentFormData`, field by field, WITHOUT ever
 * overwriting something the tenant already typed (checked per field, not just
 * "is the form empty") and WITHOUT copying anything outside the whitelist above.
 *
 * @returns {{ formData: object, employmentType: string|null, prefilled: boolean }}
 *   `prefilled` is true only when at least one field actually changed — the caller
 *   uses it to decide whether to show the "we've filled this in" notice.
 */
export function applyWorkDetailsPrefill(previousEmployment, currentFormData) {
  const baseFormData = currentFormData || {};

  if (!hasPrefillableEmployment(previousEmployment)) {
    return { formData: baseFormData, employmentType: null, prefilled: false };
  }

  const nextFormData = { ...baseFormData };
  let prefilled = false;

  for (const field of PREFILLABLE_WORK_DETAILS_FIELDS) {
    const previousValue = previousEmployment[field];
    const alreadyTyped = hasValue(baseFormData[field]);
    if (hasValue(previousValue) && !alreadyTyped) {
      nextFormData[field] = previousValue;
      prefilled = true;
    }
  }

  return {
    formData: nextFormData,
    employmentType: hasValue(previousEmployment.employmentType)
      ? previousEmployment.employmentType
      : null,
    prefilled,
  };
}
